// Types for the v2 form API response.

export interface V2QuestionOption {
  label: string;
  value: string;
}

export interface V2Question {
  id: string;
  formId: string;
  questionType: string;
  label: string;
  placeholder?: string | null;
  isRequired: boolean;
  order: number;
  pageNumber: number;
  options?: V2QuestionOption[] | null;
  // Markdown-formatted scoring criteria shown below the question label (e.g. SESSION_FEEDBACK sub-criteria).
  description?: string | null;
  // Permissive: holds type-specific rules such as minRating/maxRating, minLength/maxLength, minValue/maxValue.
  validation?: Record<string, any> | null;
  correctAnswer?: unknown;
  /** Per-question markers set by the builder, e.g. `{ isAttendanceGate: true }` on a geo capture form, `{ completionCertificateField: "certificate" }` on the completion certificate upload. */
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

export interface V2FormData {
  id: string;
  title: string;
  description?: string | null;
  questions?: V2Question[];
  tenantId?: string;
  type?: string;
  status?: string;
  settings?: Record<string, unknown>;
  designSettings?: Record<string, unknown> | null;
  // `legacyId` holds the pre-migration form id (e.g. "form-recYqaZRuo5iEPYVR"), used for legacy-specific behavior.
  metadata?: { legacyId?: string | null } | null;
  createdAt?: string;
  updatedAt?: string;
}

// A single saved answer in a submission.
export interface V2Response {
  questionId: string;
  answer: string | number | string[] | null;
  fileUrls?: string[];
  /** Per-answer extras. GEO_PICTURE puts the capture's coordinates and time here. */
  otherFields?: Record<string, unknown> | null;
  question?: V2Question;
}

/** A GEO_PICTURE answer before it is split into `answer` (the URL) and `otherFields.capture`. */
export interface GeoPictureValue {
  url: string;
  latitude: number;
  longitude: number;
  /** Radius of confidence in metres, as the browser reports it. */
  accuracy?: number | null;
  /** Metres above sea level. Null from network/wifi positioning — a useful hint the fix is not GPS. */
  altitude?: number | null;
  capturedAt: string;
  /**
   * Read-only. The place name core-api resolved and stored on `otherFields.capture.place`, present only when a
   * saved submission is pre-filled. Never sent back: `buildResponses` picks its fields explicitly.
   */
  place?: string | null;
}

// Learner draft / existing submission (GET /learner/forms/feedback-response).
// The endpoint returns the submission object directly (no wrapper).
export interface V2DraftResponse {
  id: string;
  formId: string;
  submittedBy?: string;
  status: string;
  responses: V2Response[];
  // Questions the server stripped from `responses` because the form marks them not-visible-to-student.
  // Only ever set on the receiver's read; the form definition still lists them, so the UI must skip them.
  hiddenQuestionIds?: string[];
  responseScores?: unknown;
  createdAt?: string;
  updatedAt?: string;
}

// Admin view of a user's submission (GET /tenantAdmin/forms/{id}/submissions/by-user/{userId}).
export interface V2AdminDraftResponse {
  id: string;
  formId: string;
  submittedBy: string;
  status: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  responses: V2Response[];
}

// Common prop shape for every question input component. The value itself is owned by the
// surrounding antd `Form` (keyed by `question.id`), so components only need the question + disabled.
export interface QuestionComponentProps {
  question: V2Question;
  disabled?: boolean;
  formType?: string;
  // Pre-migration form id from the form's metadata; drives legacy-specific scoring (e.g. SESSION_FEEDBACK /10 forms).
  legacyId?: string | null;
}

// Distinguishes a permission failure from other errors for the embedded load-error message,
// shown instead of ever falling through to the standalone login screen.
export const resolveLoadErrorMessage = (err: unknown): string => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 401 || status === 403) {
    return "You don't have permission to view this feedback form.";
  }
  return "Something went wrong while loading the feedback form. Please try again.";
};

/** The server's own reason for an HTTP 400 (validation) rejection, shown to the learner as-is, or undefined for any other error. */
export const clientErrorMessage = (err: unknown): string | undefined => {
  const axiosErr = err as { response?: { status?: number; data?: { error?: { message?: string } } } };
  const status = axiosErr?.response?.status;
  const serverMsg = axiosErr?.response?.data?.error?.message;
  return status === 400 && typeof serverMsg === "string" && serverMsg.trim() ? serverMsg : undefined;
};

/**
 * The message to show when a submission is rejected. An expected client-side rejection (4xx) carries
 * the server's own reason (e.g. "already shared an experience for this company and role", or
 * "resubmission is not allowed"), which says more than generic copy; unexpected, 5xx and network
 * errors get the generic message.
 */
export const resolveSubmitErrorMessage = (err: unknown): string => {
  const axiosErr = err as { response?: { status?: number; data?: { error?: { message?: string } } } };
  const status = axiosErr?.response?.status;
  const serverMsg = axiosErr?.response?.data?.error?.message;
  return status && status >= 400 && status < 500 && typeof serverMsg === "string" && serverMsg.trim()
    ? serverMsg
    : "Something went wrong while submitting. Please try again.";
};
